import { useEffect, useState } from 'react';
import { format, parseISO } from 'date-fns';
import api from '../../api/client';
import StatusBadge from '../common/StatusBadge';

const TRIGGER_TYPES = [
  { value: 'all', label: 'Todos' },
  { value: 'no_media', label: 'Sin medios disponibles' },
  { value: 'queue_empty', label: 'Cola vacía' },
  { value: 'schedule_gap', label: 'Hueco en programación' },
  { value: 'manual', label: 'Manual' },
  { value: 'error', label: 'Error' },
];

export default function FallbackLog() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [triggerFilter, setTriggerFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    fetchLogs();
  }, [triggerFilter, dateFrom, dateTo, search, page]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = { page, limit: 50 };
      if (triggerFilter !== 'all') params.trigger_type = triggerFilter;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (search) params.search = search;

      const { data } = await api.get('/api/history/fallback', { params });
      setLogs(data.logs || data);
    } catch (err) {
      console.error('[FallbackLog] Failed to fetch:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[300px] items-center justify-center">
        <p className="text-dark-400">Cargando registro de fallback...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-dark-100">Registro de Fallback</h1>
        <button onClick={fetchLogs} className="btn btn-secondary btn-sm" title="Actualizar">
          ↻
        </button>
      </div>

      <div className="flex flex-wrap gap-3">
        <input
          type="text"
          placeholder="Buscar registros..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="input max-w-sm text-sm"
        />
        <select
          value={triggerFilter}
          onChange={(e) => setTriggerFilter(e.target.value)}
          className="input text-sm w-44"
        >
          {TRIGGER_TYPES.map((t) => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </select>
        <input
          type="date"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          className="input text-sm w-40"
        />
        <input
          type="date"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          className="input text-sm w-40"
        />
      </div>

      {logs.length === 0 ? (
        <div className="py-12 text-center text-dark-400">No se encontraron eventos de fallback.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>Fecha/Hora</th>
                <th>Disparador</th>
                <th>Programa</th>
                <th>Medio Esperado</th>
                <th>Medio Fallback</th>
                <th>Duración</th>
                <th>Gestionado</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => (
                <tr key={log.id}>
                  <td className="text-sm text-dark-400">
                    {log.timestamp ? format(parseISO(log.timestamp), 'PPp') : '—'}
                  </td>
                  <td>
                    <span className="text-xs font-medium px-2 py-0.5 rounded bg-dark-700 text-warning-400">
                      {log.trigger_type || 'desconocido'}
                    </span>
                  </td>
                  <td className="text-sm text-dark-300">{log.show_name || '—'}</td>
                  <td className="text-sm text-dark-300">{log.expected_media || '—'}</td>
                  <td className="text-sm text-dark-300">{log.fallback_media || '—'}</td>
                  <td className="text-sm text-dark-400">
                    {log.duration_seconds ? `${log.duration_seconds}s` : '—'}
                  </td>
                  <td>
                    <StatusBadge status={log.is_handled ? 'completed' : 'pending'} size="sm" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {logs.length > 0 && (
        <div className="flex justify-between text-sm text-dark-400">
          <span>Página {page}</span>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1 || loading}
              className="btn btn-secondary btn-sm"
            >
              Anterior
            </button>
            <button
              onClick={() => setPage(page + 1)}
              disabled={loading}
              className="btn btn-secondary btn-sm"
            >
              Siguiente
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
